// @stories HIR-TC1, HIR-HD2
// A candidate's total out of 100, grade and status, on the candidate panel
// (server: services/candidateTotal.service.js). Read-only. When the
// vacancy's weights are not set — or it is a leadership post, or its track
// is not stated — the server's plain reason is shown instead of a number;
// nothing here works a total out.
import React, { useEffect, useState } from 'react';
import { Calculator } from 'lucide-react';
import api from '../../../services/api';

const STATUS_STYLE = {
  recommended: 'bg-green-50 text-green-800 border-green-100',
  'on hold': 'bg-yellow-50 text-yellow-800 border-yellow-100',
  'in progress': 'bg-blue-50 text-blue-800 border-blue-100',
  redirected: 'bg-purple-50 text-purple-800 border-purple-100',
  'not recommended': 'bg-red-50 text-red-800 border-red-100',
  withdrawn: 'bg-gray-50 text-gray-700 border-gray-200',
};

export default function CandidateTotal({ resumeId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let live = true;
    setData(null); setErr('');
    api.get(`/candidate-scoring/candidates/${resumeId}/total`)
      .then((r) => { if (live) setData(r.data?.data || null); })
      .catch((e) => { if (live) setErr(e.response?.data?.message || 'Could not work out the total just now.'); });
    return () => { live = false; };
  }, [resumeId]);

  if (err) return <div className="mb-6 pt-4 border-t text-sm text-red-600">{err}</div>;
  if (!data) return null;

  return (
    <div className="mb-6 pt-4 border-t" data-testid="candidate-total">
      <h4 className="font-medium text-gray-900 flex items-center gap-1.5 mb-2">
        <Calculator className="w-4 h-4 text-gray-600" /> Total
      </h4>
      {!data.ok ? (
        <p className="text-sm text-gray-600">{data.refusal}</p>
      ) : (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-lg font-semibold text-gray-900">
              {data.total == null ? '—' : data.total}<span className="text-sm font-normal text-gray-500"> / 100</span>
            </span>
            {data.grade && <span className="text-sm font-medium text-gray-800">Grade {data.grade}</span>}
            <span className={`text-xs px-2 py-0.5 rounded border capitalize ${STATUS_STYLE[data.status] || STATUS_STYLE.withdrawn}`}>{data.status}</span>
          </div>
          {data.statusWhy && <p className="text-xs text-gray-500 mt-1">{data.statusWhy}</p>}
          <ul className="mt-2 text-xs text-gray-700 space-y-0.5">
            {(data.stages || []).map((s) => (
              <li key={s.stageId} className="flex justify-between gap-2">
                <span>
                  {s.label}
                  {s.note ? <span className="text-orange-700"> · {s.note}</span> : null}
                  {s.hrRuling ? <span className="text-gray-500"> · HR {s.hrRuling.decision}</span> : null}
                  {s.missing?.length ? <span className="text-gray-500"> · no mark from {s.missing.join(', ')}</span> : null}
                </span>
                <span className="text-gray-500 whitespace-nowrap">
                  {s.percent == null ? 'not done' : `${s.percent}%`}{s.weight ? ` × ${s.weight}` : ' · no weight'}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
